/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2024-01-17 16:50:02
 */
import React, { useState } from 'react'
import { Modal, App, Upload } from 'antd'
import type { UploadFile, RcFile } from 'antd/es/upload/interface'
import Excel from 'exceljs'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { setLoading } from '../../../actions/main'
import { InboxOutlined } from '@ant-design/icons'

const { Dragger } = Upload

interface LocationItem {
  content: string
  address: string
  lat: string
  lng: string
}

interface UploadComponentProps {
  getLocationsByAddress: (locations: LocationItem[]) => void
  reportSaClickEvent: (event: string) => void
  reportSaUploadEvent: (event: string) => void
  afterUpload: (fileName: string) => void
}

// exceljs 单元格值类型
type CellValue =
  | string
  | number
  | { richText: Array<{ text: string }> }
  | null
  | undefined

const UploadComponent: React.FC<UploadComponentProps> = ({
  getLocationsByAddress,
  reportSaClickEvent,
  reportSaUploadEvent,
  afterUpload
}) => {
  const dispatch = useAppDispatch()
  const { message } = App.useApp()
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const itemCnt = useAppSelector((state) => state.group.itemCnt)
  const config = useAppSelector((state) => state.main.config)
  const itemLimit = parseInt(
    String(itemCnt || config?.normal_group_option_limit || 1000)
  )

  const clearPage = () => {
    setFileList([])
  }

  const getCellPlainValue = (value: CellValue): string => {
    if (typeof value === 'string' || typeof value === 'number') {
      return value.toString()
    }
    if (typeof value === 'object' && value !== null && 'richText' in value) {
      const texts = value.richText.map((richText) => richText.text)
      return texts.join('')
    }
    return ''
  }

  const parseExcel = async (file: RcFile | File) => {
    const workbook = new Excel.Workbook()
    const reader = new FileReader()
    reader.onloadend = function () {
      const arrayBuffer = reader.result as ArrayBuffer
      workbook.xlsx.load(arrayBuffer).then(function (loadedWorkbook) {
        const locations: LocationItem[] = []
        const sheet = loadedWorkbook.worksheets[0]
        if (!sheet) {
          reportSaUploadEvent('formatError')
          Modal.error({
            title: '解析不到文件内的数据，请下载模板，按照模板填写信息后提交',
            onOk: clearPage,
            zIndex: 9999
          })
        } else {
          if (sheet.actualRowCount <= 2) {
            reportSaUploadEvent('noData')
            Modal.error({
              title: '表格中无数据，请按照模板填写信息后提交。',
              onOk: clearPage,
              zIndex: 9999
            })
            return false
          }
          if (sheet.actualRowCount > Number(itemLimit) + 2) {
            reportSaUploadEvent('exceedLimit')
            Modal.error({
              title: `一次最多导入${itemLimit}个选项！`,
              onOk: clearPage,
              zIndex: 9999
            })
          } else {
            dispatch(setLoading(true))

            loadedWorkbook.worksheets[0].eachRow(function (row, rowNumber) {
              if (rowNumber > 2) {
                let content = row.values[1]
                  ? getCellPlainValue(row.values[1] as CellValue)
                  : ''
                let address = row.values[2]
                  ? getCellPlainValue(row.values[2] as CellValue)
                  : ''
                let lng = row.values[3]
                  ? getCellPlainValue(row.values[3] as CellValue)
                  : ''
                let lat = row.values[4]
                  ? getCellPlainValue(row.values[4] as CellValue)
                  : ''

                content = content.trim().replace(/\n/g, '')
                address = address.trim().replace(/\n/g, '')
                lat = lat.trim().replace(/\n/g, '')
                lng = lng.trim().replace(/\n/g, '')

                locations.push({
                  content,
                  address,
                  lat,
                  lng
                })
              }
            })

            // 数据解析完成调用函数传递列表
            getLocationsByAddress(locations)
          }
        }
      })
    }
    reader.readAsArrayBuffer(file)
  }

  const beforeUpload = (file: RcFile) => {
    reportSaClickEvent('chooseFile')
    const isExcelFile =
      file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel' ||
      file.name.split('.')[1]?.indexOf('xls') > -1

    if (!isExcelFile) {
      message.error('文件格式错误，仅支持xls、xlsx文件！')
    } else if (file.size / 1024 / 1024 > 100) {
      message.error('上传文件过大，文件最大支持100MB！')
    } else {
      setFileList([file])
      afterUpload(file.name)
      parseExcel(file)
    }
    return false
  }

  const uploadProps = {
    accept:
      'application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    name: 'file',
    multiple: false,
    beforeUpload,
    onRemove: clearPage,
    fileList
  }

  return (
    <Dragger {...uploadProps}>
      <div className="upload-drag-wrapper">
        <InboxOutlined
          style={{
            fontSize: 48,
            color: '#1890FF'
          }}
        />
        <p className="upload-text">点击选择文件或将文件拖拽至此处上传</p>
        <p className="upload-hint">仅支持xls、xlsx格式文件</p>
      </div>
    </Dragger>
  )
}

export default UploadComponent
